/**
 * Platform-wide weekend trading pause (effective Friday, July 31st 2026).
 *
 * From Friday 8:00 PM ET no new orders can be placed on any non-crypto market.
 * Trading resumes on Sunday — 6:00 PM ET for ES, NQ, the commodity futures and
 * FX, and 8:00 PM ET for everything else non-crypto (single stocks, ETFs, RTY).
 * Crypto is unaffected and keeps trading 24/7.
 *
 * The pause applies to EVERY account type (self-deposit, funded, eval) and
 * blocks reduce-only orders too: open positions are NOT force closed, they
 * simply cannot be modified until the market reopens.
 *
 * All times are New York wall-clock (ET), DST-aware.
 *
 * This is a client-side guard for UX only — the backend stays the source of
 * truth for what is actually accepted.
 */

import { AssetType } from '@/types';

/** Master switch — flip to `false` to lift the pause everywhere. */
export const WEEKEND_TRADING_PAUSE_ENABLED = true;

export enum WeekendGroup {
  /** Trades through the weekend — never paused. */
  ALWAYS_OPEN = 'alwaysOpen',
  /** Paused Fri 8:00 PM ET, reopens Sun 6:00 PM ET — ES, NQ, commodities and FX. */
  FUTURES = 'futures',
  /** Paused Fri 8:00 PM ET, reopens Sun 8:00 PM ET — everything else non-crypto. */
  EQUITIES = 'equities',
}

/**
 * Symbols that stay tradable through the weekend, whatever their asset class.
 * Kept explicit so classification does not depend on `exchangeInfo` having
 * loaded; any market with `asset === CRYPTO` is allowed as well (see
 * {@link getWeekendGroup}), so newly listed crypto does not need an entry here.
 */
export const WEEKEND_ALLOWED_SYMBOLS = new Set([
  'AAVE-PERP',
  'ASTER-PERP',
  'AVAX-PERP',
  'BNB-PERP',
  'BTC-PERP',
  'BTC.D-PERP',
  'DOGE-PERP',
  'ENA-PERP',
  'ETH-PERP',
  'FARTCOIN-PERP',
  'GRASS-PERP',
  'HYPE-PERP',
  'JUP-PERP',
  'kBONK-PERP',
  'kPEPE-PERP',
  'NEAR-PERP',
  'ONDO-PERP',
  'OTHERS.D-PERP',
  'PAXG-PERP',
  'SOL-PERP',
  'STABLE.D-PERP',
  'SUI-PERP',
  'TAO-PERP',
  'TON-PERP',
  'WIF-PERP',
  'WLD-PERP',
  'XRP-PERP',
  'ZK-PERP',
  'ZRO-PERP',
]);

/**
 * Markets that reopen Sunday 6:00 PM ET and have to be matched by SYMBOL: ES,
 * NQ and the commodity futures. FX joins them but is matched on `asset` in
 * {@link getWeekendGroup}. Everything else non-crypto waits until 8:00 PM ET.
 *
 * This list is exhaustive and matched by symbol because the v2 `exchangeInfo`
 * this frontend reads carries no categories, and `asset` is `stock` for every
 * entry below. Verified against the full 549-symbol production universe.
 *
 * Ticker traps, all deliberately absent:
 *  - `ES-USD-PERP` is Eversource Energy; the S&P 500 E-mini is `SPX-USD-PERP`.
 *  - `NOK-PERP` is Nokia, not a future (`NOK-USD-PERP` is the Norwegian krone).
 *  - `CL-USD-PERP` is Colgate-Palmolive; crude oil is `CL-PERP`.
 *  - `RTY-USD-PERP` (Russell 2000 E-mini) reopens with the equities at 8:00 PM.
 */
export const WEEKEND_FUTURES_SYMBOLS = new Set([
  'SPX-USD-PERP', // ES — S&P 500 E-mini futures
  'NDX-USD-PERP', // NQ — Nasdaq-100 E-mini futures
  'GC-PERP', // Gold
  'SI-PERP', // Silver
  'HG-PERP', // Copper
  'PL-PERP', // Platinum
  'CL-PERP', // Crude oil
  'BZ-PERP', // Brent
  'NG-PERP', // Natural gas
]);

/**
 * Funded accounts additionally cannot trade the CME index perps over the
 * weekend break. This predates the platform-wide pause and is INDEPENDENT of
 * {@link WEEKEND_TRADING_PAUSE_ENABLED} — turning the pause off must not hand
 * funded traders back a market they were never allowed to trade.
 *
 * Eval accounts are not covered (matches the original rule).
 */
export const FUNDED_CLOSED_SYMBOLS = new Set([
  'SPX-USD-PERP', // ES
  'NDX-USD-PERP', // NQ
]);

const NY_TIME_ZONE = 'America/New_York';

// Weekdays are 0-indexed from Sunday, times are ET wall-clock.
const CLOSE_WEEKDAY = 5; // Friday
const CLOSE_HOUR = 20; // 8:00 PM ET
const REOPEN_WEEKDAY = 0; // Sunday

const REOPEN_HOUR: Record<WeekendGroup, number> = {
  [WeekendGroup.ALWAYS_OPEN]: 0,
  [WeekendGroup.FUTURES]: 18, // 6:00 PM ET
  [WeekendGroup.EQUITIES]: 20, // 8:00 PM ET
};

/** The funded break follows the CME weekend: Fri 8:00 PM -> Sun 6:00 PM ET. */
const FUNDED_REOPEN_HOUR = 18;

/** Groups that observe the pause — everything except {@link WeekendGroup.ALWAYS_OPEN}. */
export const WEEKEND_PAUSED_GROUPS = [
  WeekendGroup.FUTURES,
  WeekendGroup.EQUITIES,
] as const;

const WEEKDAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Weekend group for a symbol. `asset` comes from `marketSpec.asset` and may be
 * `undefined` before `exchangeInfo` resolves — an unknown market is treated as
 * an equity (i.e. paused), which self-corrects once the spec arrives.
 */
export function getWeekendGroup(
  symbol: string,
  asset: AssetType | undefined,
): WeekendGroup {
  if (asset === AssetType.CRYPTO) return WeekendGroup.ALWAYS_OPEN;
  if (WEEKEND_ALLOWED_SYMBOLS.has(symbol)) return WeekendGroup.ALWAYS_OPEN;
  if (WEEKEND_FUTURES_SYMBOLS.has(symbol)) return WeekendGroup.FUTURES;
  // FX reopens with the CME weekend break, matched on `asset` rather than a
  // symbol list: every FX pair carries `asset === FOREX`, which also keeps the
  // `NOK-USD-PERP` (krone, FX) / `NOK-PERP` (Nokia, stock) pair apart.
  if (asset === AssetType.FOREX) return WeekendGroup.FUTURES;
  return WeekendGroup.EQUITIES;
}

/** True when the symbol observes the weekend pause at all. */
export function isWeekendPausedMarket(
  symbol: string,
  asset: AssetType | undefined,
): boolean {
  return (
    WEEKEND_TRADING_PAUSE_ENABLED &&
    getWeekendGroup(symbol, asset) !== WeekendGroup.ALWAYS_OPEN
  );
}

const nyPartsFormat = new Intl.DateTimeFormat('en-US', {
  timeZone: NY_TIME_ZONE,
  weekday: 'short',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

type NyParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  weekday: number;
};

function getNyParts(date: Date): NyParts {
  const parts: Partial<Record<Intl.DateTimeFormatPartTypes, string>> = {};
  for (const { type, value } of nyPartsFormat.formatToParts(date)) {
    parts[type] = value;
  }
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour),
    minute: Number(parts.minute),
    weekday: WEEKDAY_NAMES.indexOf(parts.weekday ?? ''),
  };
}

// Resolves a New York wall-clock time to an absolute instant. The two
// correction passes account for the ET UTC offset, including DST shifts.
function nyWallClockToDate(
  year: number,
  month: number,
  day: number,
  hour: number,
): Date {
  const target = Date.UTC(year, month - 1, day, hour);
  let ts = target;
  for (let pass = 0; pass < 2; pass++) {
    const p = getNyParts(new Date(ts));
    const wall = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
    ts += target - wall;
  }
  return new Date(ts);
}

function nextNyWeekdayTime(from: Date, weekday: number, hour: number): Date {
  const p = getNyParts(from);
  for (let addDays = 0; addDays <= 7; addDays++) {
    if ((p.weekday + addDays) % 7 !== weekday) continue;
    // Date.UTC normalizes day overflow into the next month/year.
    const date = new Date(Date.UTC(p.year, p.month - 1, p.day + addDays));
    const candidate = nyWallClockToDate(
      date.getUTCFullYear(),
      date.getUTCMonth() + 1,
      date.getUTCDate(),
      hour,
    );
    if (candidate.getTime() > from.getTime()) return candidate;
  }
  throw new Error('unreachable: no weekday match within a week');
}

export type WeekendPauseState = {
  /** Trading is paused for this group right now. */
  isPaused: boolean;
  /** When `isPaused` flips — the reopen when paused, the next Friday close otherwise. */
  changesAt: Date;
};

/** Closed from Friday `CLOSE_HOUR` ET until Sunday `reopenHour` ET. */
function getWindowState(reopenHour: number, now: Date): WeekendPauseState {
  const p = getNyParts(now);
  const minuteOfWeek = p.weekday * 1440 + p.hour * 60 + p.minute;
  const closeMinute = CLOSE_WEEKDAY * 1440 + CLOSE_HOUR * 60;
  const reopenMinute = REOPEN_WEEKDAY * 1440 + reopenHour * 60;
  const isPaused = minuteOfWeek >= closeMinute || minuteOfWeek < reopenMinute;

  return {
    isPaused,
    changesAt: isPaused
      ? nextNyWeekdayTime(now, REOPEN_WEEKDAY, reopenHour)
      : nextNyWeekdayTime(now, CLOSE_WEEKDAY, CLOSE_HOUR),
  };
}

/**
 * Pause state for a weekend group at `now`. `ALWAYS_OPEN` never pauses, and the
 * countdown still points at the next Friday close so callers can render a
 * uniform "closes in" label without special-casing.
 */
export function getWeekendPauseState(
  group: WeekendGroup,
  now: Date,
): WeekendPauseState {
  if (!WEEKEND_TRADING_PAUSE_ENABLED || group === WeekendGroup.ALWAYS_OPEN) {
    return {
      isPaused: false,
      changesAt: nextNyWeekdayTime(now, CLOSE_WEEKDAY, CLOSE_HOUR),
    };
  }

  return getWindowState(REOPEN_HOUR[group], now);
}

/** Why order placement is blocked. The platform pause wins when both apply. */
export type WeekendBlockReason = 'none' | 'pause' | 'funded';

export type WeekendBlockState = {
  group: WeekendGroup;
  /** Some weekend rule applies to this market for this account. */
  isRestricted: boolean;
  /** Blocked right now. */
  isBlocked: boolean;
  reason: WeekendBlockReason;
  /** When `isBlocked` flips. */
  changesAt: Date;
  /** Sunday reopen time, ET, for user-facing copy ('' when unrestricted). */
  reopenLabel: string;
};

/**
 * Combined weekend restriction for a symbol and account: the platform-wide
 * pause and the funded-only CME break, resolved together. The two rules are
 * independent — a funded trader stays blocked on ES/NQ even with the platform
 * pause switched off.
 *
 * `pauseEnabled` exists so tests can exercise the funded rule on its own.
 */
export function getWeekendBlockState({
  symbol,
  asset,
  isFunded,
  now,
  pauseEnabled = WEEKEND_TRADING_PAUSE_ENABLED,
}: {
  symbol: string;
  asset: AssetType | undefined;
  isFunded: boolean;
  now: Date;
  pauseEnabled?: boolean;
}): WeekendBlockState {
  const group = getWeekendGroup(symbol, asset);

  const pauseHour =
    pauseEnabled && group !== WeekendGroup.ALWAYS_OPEN
      ? REOPEN_HOUR[group]
      : null;
  const fundedHour =
    isFunded && FUNDED_CLOSED_SYMBOLS.has(symbol) ? FUNDED_REOPEN_HOUR : null;

  if (pauseHour === null && fundedHour === null) {
    return {
      group,
      isRestricted: false,
      isBlocked: false,
      reason: 'none',
      changesAt: nextNyWeekdayTime(now, CLOSE_WEEKDAY, CLOSE_HOUR),
      reopenLabel: '',
    };
  }

  const pause = pauseHour === null ? null : getWindowState(pauseHour, now);
  const funded = fundedHour === null ? null : getWindowState(fundedHour, now);
  const isBlocked = !!pause?.isPaused || !!funded?.isPaused;

  // While blocked, count down to the LAST of the active reopens so the label
  // never promises an opening that another rule still withholds.
  const changesAt = isBlocked
    ? new Date(
        Math.max(
          pause?.isPaused ? pause.changesAt.getTime() : 0,
          funded?.isPaused ? funded.changesAt.getTime() : 0,
        ),
      )
    : new Date(
        Math.min(
          pause?.changesAt.getTime() ?? Infinity,
          funded?.changesAt.getTime() ?? Infinity,
        ),
      );

  return {
    group,
    isRestricted: true,
    isBlocked,
    reason: pause?.isPaused ? 'pause' : funded?.isPaused ? 'funded' : 'none',
    changesAt,
    // The later of the active reopens — that is when the market is really back.
    reopenLabel: formatEtHour(Math.max(pauseHour ?? 0, fundedHour ?? 0)),
  };
}

const formatEtHour = (hour: number): string =>
  `${hour > 12 ? hour - 12 : hour}:00 ${hour >= 12 ? 'PM' : 'AM'}`;

/**
 * `2d 3h` / `4h 7m` / `1m 52s` / `52s` — countdown for the market-info column.
 *
 * Zero units are dropped rather than padded, and seconds appear once the wait
 * is under an hour. That last part matters: with minute granularity the column
 * read `0h 00m` for the whole final minute while orders were still blocked, so
 * "it says zero but I still can't trade" was a real complaint. Now the display
 * only reaches `0s` at the instant the market actually reopens.
 */
export function formatWeekendCountdown(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const days = Math.floor(total / 86_400);
  const hours = Math.floor((total % 86_400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;

  const parts: string[] = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);
  // Seconds would be noise next to hours or days; below an hour they are the
  // whole point.
  if (days === 0 && hours === 0 && (seconds || parts.length === 0)) {
    parts.push(`${seconds}s`);
  }

  return parts.join(' ');
}

/**
 * How often the countdown must re-render to stay truthful: every second while
 * seconds are on screen, lazily otherwise.
 */
export function getWeekendCountdownTickMs(ms: number): number {
  return ms < 3_600_000 ? 1_000 : 20_000;
}
